import React, { createContext, useContext, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

const API_SCOPE = 'api://' + process.env.REACT_APP_AAD_CLIENT_ID + '/user_impersonation';

interface AuthTokenContextType {
  getToken: () => Promise<string | undefined>;
  isAuthenticated: boolean;
  account: { username?: string; localAccountId: string } | null;
}

const AuthTokenContext = createContext<AuthTokenContextType | null>(null);

export const AuthTokenProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = accounts[0] || null;

  const getToken = useCallback(async () => {
    if (accounts.length > 0) {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: [API_SCOPE],
          account: accounts[0]
        });
        return tokenResponse.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const response = await instance.acquireTokenPopup({
              scopes: [API_SCOPE]
            });
            return response.accessToken;
          } catch (e) {
            console.error('Error acquiring token popup:', e);
            return undefined;
          }
        }
        console.error('Error acquiring token:', error);
        return undefined;
      }
    }
    return undefined;
  }, [instance, accounts]);

  return (
    <AuthTokenContext.Provider value={{
      getToken,
      isAuthenticated: accounts.length > 0,
      account
    }}>
      {children}
    </AuthTokenContext.Provider>
  );
};

export const useAuthToken = () => {
  const context = useContext(AuthTokenContext);
  if (!context) {
    throw new Error('useAuthToken must be used within an AuthTokenProvider');
  }
  return context;
}; 